'use client';

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FileText, Upload } from 'lucide-react';
import { useNavigate } from "react-router-dom";

interface InvoiceCreationMethodSelectorProps {
  onSelectMethod: (method: 'generate' | 'have-invoice') => void;
}

export const InvoiceCreationMethodSelector: React.FC<InvoiceCreationMethodSelectorProps> = ({
  onSelectMethod
}) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        {/* Generate Invoice Card */}
        <Box
          sx={{
            cursor: "pointer",
            bgcolor: "background.paper",
            border: 2,
            borderColor: "divider",
            borderRadius: 1,
            p: 3,
            transition: "all .2s",
            "&:hover": {
              borderColor: "primary.main",
              boxShadow: 3,
            },
            "&:hover [data-icon]": {
              bgcolor: (t) => alpha(t.palette.primary.main, 0.2),
            },
          }}
          onClick={() => navigate('/crm/invoices/generate')}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 2 }}>
            <Box
              data-icon
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "primary.main",
                transition: "background-color .15s",
              }}
            >
              <FileText size={24} />
            </Box>
            <Box>
              <Typography component="h3" sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>Generate Invoice</Typography>
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Auto-generate a professional invoice</Typography>
            </Box>
          </Box>
        </Box>

        {/* I Have Invoice Card */}
        <Box
          sx={{
            cursor: "pointer",
            bgcolor: "background.paper",
            border: 2,
            borderColor: "divider",
            borderRadius: 1,
            p: 3,
            transition: "all .2s",
            "&:hover": {
              borderColor: "success.main",
              boxShadow: 3,
            },
            "&:hover [data-icon]": {
              bgcolor: (t) => alpha(t.palette.success.main, 0.2),
            },
          }}
          onClick={() => onSelectMethod('have-invoice')}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 2 }}>
            <Box
              data-icon
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: (t) => alpha(t.palette.success.main, 0.12),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "success.main",
                transition: "background-color .15s",
              }}
            >
              <Upload size={24} />
            </Box>
            <Box>
              <Typography component="h3" sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>I Have Invoice</Typography>
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Upload or enter details of existing invoice</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
